<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        
        <v-content class="bg-container">
            <v-layout fill-height row wrap>

                <!-- Left/top Side: Ads -->
                <v-flex v-if="!isMobile" xs12 sm6 class="ads-section">
                    <div class="ads-bg">
                        <public-notifications></public-notifications>
                    </div>
                </v-flex>
                
                <!-- Right/bottom Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout fill-height align-center justify-center>
                        <div class="login-container">
                            <v-card class="mx-auto login-card" max-width="500">
                                <v-form @submit.prevent="executeRecaptcha" ref="form">
                                    <v-card-text>
                                        <h3 v-if="isRecaptchaLoaded" class="text-xs-center">LOGIN</h3>
                                        <h3 v-if="!isRecaptchaLoaded" class="text-xs-center">Loading components...</h3>

                                        <v-alert v-if="error" :value="true" color="warning" icon="warning" class="rounded-4 mt-10" outline>
                                            {{errorMessage}}
                                        </v-alert>
                                        
                                        <v-alert v-if="slowInternet" :value="true" color="warning" icon="warning" class="rounded-4 mt-10" outline>
                                            Internet connection is slow. Some features may not work properly. 
                                        </v-alert>

                                        <v-text-field
                                            class="body-1 orange--text mt-20"
                                            prepend-inner-icon="email"
                                            v-model="email"
                                            label="Email"
                                            :rules="[rules.required,rules.email,rules.min,rules.max]"
                                            type="text"
                                            :disabled="disabled||isNoInternet"
                                        ></v-text-field>

                                        <v-text-field
                                            class="body-1"
                                            prepend-inner-icon="password"
                                            v-model="password"
                                            :append-icon="show1 ? 'visibility_off' : 'visibility'"
                                            :rules="[rules.required,rules.counter]"
                                            :type="show1 ? 'text' : 'password'"
                                            label="Password"
                                            @click:append="show1 = !show1"
                                            :disabled="disabled||isNoInternet"
                                        ></v-text-field>

                                        <v-btn v-if="!slowInternet" :color="welcomeButtonColor" type="submit" :loading="loading" block dark :disabled="isNoInternet">
                                            <v-icon>login</v-icon>&nbsp;&nbsp;Login
                                        </v-btn>
                                        <v-btn v-if="slowInternet" :color="welcomeButtonColor" block dark @click="reloadPage">Refresh Page</v-btn>
                                    </v-card-text>
                                </v-form>
                                <v-divider></v-divider>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn small flat to="/PasswordRecovery" :disabled="disabled||isNoInternet||!isRecaptchaLoaded" class="caption mr-5">I forgot my password</v-btn>
                                </v-card-actions>
                            </v-card>
                            <v-card class="mt-2 mx-auto transparent elevation-0 text-xs-center" max-width="420">
                                <terms-privacy></terms-privacy>
                            </v-card>
                        </div>
                        <div class="footer-section caption">
                            <div class="white--text text-xs-center">&copy; Copyright 2024 SweldoNow OPC. All Rights Reserved.</div>
                            <other-legal-docs v-if="!isMobile"></other-legal-docs>
                        </div>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha" @recaptcha-loaded="recaptchaLoaded"></recaptcha>
        
        <v-snackbar v-model="isNoInternet" color="warning" :bottom="y === 'top'" :left="x === 'left'" :multi-line="mode === 'multi-line'"
            :right="x === 'right'" :timeout="0" :top="y === 'top'" :vertical="mode === 'vertical'"
            >You have no internet connection.
            <v-btn color="primary" flat @click="isNoInternet=false">Close</v-btn>
        </v-snackbar>
    </v-app>
</template>
<script>

import '@/assets/css/public.css'
import '@/assets/css/season.css'
import auth from "@/auth/auth.js"
import publicPages from '@/helpers/public-pages.js'
import { getUAData } from '@/helpers/user-agent.js'
import { parseApiError } from '@/helpers/login-error-handler'

export default {
    name: 'Login',
    metaInfo: {
        title: 'Login',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Login' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'recaptcha': () => import('@/components/ReCaptcha.vue'),
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'),       
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
    },
    data () {
        return {
            loading: true,
            disabled: false,
            error: false,
            errorMessage: null,
            show1: false,
            slowInternet: false,

            //for refactor - create reusable rules-helper.js 
            rules: {
                required: value => !!value || "Required.",
                min: v => v.length >= 12 || "Min 12 characters",
                counter: value => value.length <= 50 || "Max 50 characters",
                max: value => value.length <= 50 || "Max 50 characters",
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return pattern.test(value) || "Invalid e-mail.";
                }
            },
            email: "",
            password: "",
            isNoInternet: false,
            isRecaptchaLoaded: false,
            mode: null,
            x: null,
            y: null,
            recaptchaLoadingTimer: null,
            userAgent: null,
         }
    },
    methods: {
        // =========================
        // UI HELPERS
        // =========================
        setLoadingState(isLoading) {
            this.loading = isLoading
            this.disabled = isLoading
        },
        showError(message) {
            this.error = true
            this.errorMessage = message
        },
        clearError() {
            this.error = false
            this.errorMessage = null
        },
        
        // =========================
        // RECAPTCHA
        // =========================        
        submitReCaptcha(response) {
            this.recaptchResponse = response;   
            this.login()
        },
        executeRecaptcha() {
            if (this.$refs.form.validate()) {
                this.loading = true;

                // execute the recaptcha widget
                this.$refs.recaptcha.execute()
                this.loading = false;
            }
        },
        recaptchaLoaded() {
            this.loading = false
            this.isRecaptchaLoaded = true
        },
        checkRecaptcha() {
            if (!this.isRecaptchaLoaded) {
                this.slowInternet = true
            }else{
                this.slowInternet = false
                clearInterval(this.recaptchaLoadingTimer)
            }
        },

        // =========================
        // LOGIN FLOW
        // =========================
        async login() {
            try {
                this.clearError()

                if (!this.$refs.form.validate()) return

                this.setLoadingState(true)

                const payload = this.buildLoginPayload()
                
                await auth.login(payload, false)

                // ===== HANDLE POST LOGIN =====
                if (auth.requiresChangePassword()) {
                    
                    //if change password has been auto triggered after login eg first login
                    //then must restrict skipping change password
                    auth.deleteToken()

                    // return this.$router.push("/changepassword")
                    return this.$router.push({
                        name: 'ChangePassword', //don't pass the path as this is validated in ChangePassword
                        query: { redirect: this.$route.query.redirect }, // Keep passing it
                        params: { fromLoginFlow: true }, 
                    });
                }

                this.handlePostLoginNavigation()

            } catch (error) {
                //this will handle all non 2xx response codes
                this.showError(parseApiError(error))
            } finally {
                this.setLoadingState(false)
            }
        },
        buildLoginPayload() {
            return {
                email: this.email,
                password: this.password,
                recaptchaResponse: this.recaptchResponse,
                browserName: this.userAgent.browser.name,
                deviceModel: this.userAgent.device.model,
                deviceType: this.userAgent.device.type,
                deviceVendor: this.userAgent.device.vendor,
                oSName: this.userAgent.os.name,
                oSVersion: this.userAgent.os.version,
                deviceFingerprint: this.userAgent.fingerprint,
                deviceName: this.userAgent.deviceName,
                sourceType: 'Customer'
            }
        },
        handlePostLoginNavigation() {
            // ===== 2FA FLOW =====
            if (this.$store.state.twoFA) {
                if (!this.$store.state.twoFAVerified) {
                    auth.deleteToken()
                    // return this.$router.push("/otp")
                    return this.$router.push({
                        path: '/otp',
                        query: { redirect: this.$route.query.redirect } // keep passing target url
                    });
                }

                return this.redirectAfterLogin()
            }

            // ===== NEW DEVICE FLOW =====
            if (this.$store.state.newDevice) {
                auth.deleteToken()
                // return this.$router.push("/otp")
                return this.$router.push({
                    path: '/otp',
                    query: { redirect: this.$route.query.redirect } // keep passing target url
                });
            }

            auth.clearLoginDetails()
            this.redirectAfterLogin()
        },
        redirectAfterLogin() {
            if (auth.onBoarded()) {
                const destination = this.$route.query.redirect || '/'
                this.$router.push(destination)
            } else {
                this.$router.push("/features")
            }
        },

        // =========================
        // CONNECTION
        // =========================
        updateConnectionStatus() {
            this.isNoInternet = !navigator.onLine;
        },
        reloadPage() {
            window.location.reload()
        },
    },
    async created() {
        this.userAgent = await getUAData()

        window.addEventListener('online', this.updateConnectionStatus)
        window.addEventListener('offline', this.updateConnectionStatus)

        this.updateConnectionStatus()

        //if recaptcha is not loaded in 5secs, show slow internet
        this.recaptchaLoadingTimer = setInterval(this.checkRecaptcha, parseInt(5000))  
    },
    computed: {
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcomeSectionClass() {
            return this.seasonDetails.className;
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor;
        },
    }
}
</script>